import { ArrowLeft, Loader2, Minus, Plus, ShieldCheck, ShoppingBag, Trash2 } from 'lucide-react';
import { useShopCheckout } from '../../hooks/useShopify';
import { formatShopifyMoney, shopifyConfigError } from '../../lib/shopify';
import { useAuthStore } from '../../store/authStore';
import { useShopStore } from '../../store/shopStore';

interface ShopCartScreenProps {
  onNavigate: (screen: any, params?: any) => void;
}

export default function ShopCartScreen({ onNavigate }: ShopCartScreenProps) {
  const { user } = useAuthStore();
  const { startCheckout, error, isLoading, isConfigured } = useShopCheckout();
  const lines = useShopStore((state) => state.lines);
  const setQuantity = useShopStore((state) => state.setQuantity);
  const removeItem = useShopStore((state) => state.removeItem);
  const clearCart = useShopStore((state) => state.clearCart);

  const subtotalAmount = lines.reduce((sum, line) => sum + line.priceAmount * line.quantity, 0);
  const currencyCode = lines[0]?.currencyCode ?? 'AED';
  const lineCount = lines.reduce((sum, line) => sum + line.quantity, 0);
  const estimatedAverage = lineCount > 0 ? subtotalAmount / lineCount : 0;

  const checkout = async () => {
    if (lines.length === 0) return;
    const { data, error: checkoutError } = await startCheckout(
      lines.map((line) => ({ merchandiseId: line.merchandiseId, quantity: line.quantity })),
      user?.email,
    );
    if (checkoutError || !data) return;
    window.location.assign(data.checkoutUrl);
  };

  return (
    <div className="w-full min-h-screen bg-background pb-28">
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => onNavigate('shop')}
          className="w-10 h-10 rounded-full hover:bg-secondary flex items-center justify-center"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg">Cart</h1>
          <p className="text-xs text-muted-foreground">{lineCount} item{lineCount === 1 ? '' : 's'}</p>
        </div>
        {lines.length > 0 && (
          <button
            onClick={clearCart}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Clear
          </button>
        )}
      </div>

      <div className="px-6 py-6 space-y-4">
        {lines.length > 0 && (
          <div className="overflow-hidden rounded-[2rem] bg-[radial-gradient(circle_at_top_left,_rgba(198,248,6,0.24),_transparent_35%),linear-gradient(135deg,#1A6B4F_0%,#18260d_100%)] p-5 text-white">
            <div className="text-[11px] uppercase tracking-[0.2em] opacity-75">Ready for checkout</div>
            <div className="mt-3 flex items-end justify-between gap-4">
              <div>
                <div className="text-3xl leading-none">{formatShopifyMoney({ amount: String(subtotalAmount), currencyCode })}</div>
                <div className="mt-2 text-sm opacity-80">
                  {lineCount} item{lineCount === 1 ? '' : 's'} in cart{user?.email ? ` for ${user.email}` : ''}
                </div>
              </div>
              <div className="rounded-2xl bg-white/10 px-3 py-2 text-right">
                <div className="text-[11px] uppercase tracking-[0.18em] opacity-75">Avg/item</div>
                <div className="mt-1 text-sm">{formatShopifyMoney({ amount: String(estimatedAverage), currencyCode })}</div>
              </div>
            </div>
          </div>
        )}

        {!isConfigured && (
          <MessageCard title="Shop not configured" body={shopifyConfigError ?? 'Shopify env vars are missing.'} />
        )}

        {error && <MessageCard title="Checkout could not start" body={error.message} />}

        {lines.length === 0 ? (
          <div className="bg-card border border-border rounded-3xl p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-secondary mx-auto mb-4 flex items-center justify-center">
              <ShoppingBag className="w-7 h-7 text-muted-foreground" />
            </div>
            <div className="text-lg mb-1">Your cart is empty</div>
            <div className="text-sm text-muted-foreground mb-5">Add products from the shop to start checkout.</div>
            <button onClick={() => onNavigate('shop')} className="h-11 px-5 rounded-xl bg-[#1A6B4F] text-white text-sm">
              Continue shopping
            </button>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {lines.map((line) => (
                <div key={line.merchandiseId} className="bg-card border border-border rounded-3xl p-4 flex gap-3">
                  <div className="w-24 h-24 rounded-2xl bg-secondary overflow-hidden shrink-0">
                    {line.imageUrl ? (
                      <img src={line.imageUrl} alt={line.imageAlt ?? line.productTitle} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">No image</div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium leading-tight">{line.productTitle}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {line.variantTitle === 'Default Title' ? 'Standard' : line.variantTitle}
                    </div>
                    <div className="text-sm text-[#1A6B4F] mt-2">
                      {formatShopifyMoney({ amount: String(line.priceAmount), currencyCode: line.currencyCode })}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Line total {formatShopifyMoney({ amount: String(line.priceAmount * line.quantity), currencyCode: line.currencyCode })}
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setQuantity(line.merchandiseId, line.quantity - 1)}
                          className="w-8 h-8 rounded-full border border-border hover:bg-secondary flex items-center justify-center"
                          aria-label="Decrease quantity"
                        >
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <div className="w-8 text-center text-sm">{line.quantity}</div>
                        <button
                          onClick={() => setQuantity(line.merchandiseId, Math.min(20, line.quantity + 1))}
                          className="w-8 h-8 rounded-full border border-border hover:bg-secondary flex items-center justify-center"
                          aria-label="Increase quantity"
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <button
                        onClick={() => removeItem(line.merchandiseId)}
                        className="w-8 h-8 rounded-full hover:bg-secondary flex items-center justify-center text-muted-foreground"
                        aria-label="Remove item"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-card border border-border rounded-3xl p-5 space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium">
                <ShieldCheck className="w-4 h-4 text-[#1A6B4F]" />
                Shopify handles final payment securely
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span>
                  {formatShopifyMoney({ amount: String(subtotalAmount), currencyCode })}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Items</span>
                <span>{lineCount}</span>
              </div>
              <div className="text-xs text-muted-foreground">
                Shipping, taxes, discounts, and final payment are handled on Shopify checkout.
              </div>
              <button
                onClick={checkout}
                disabled={isLoading || !isConfigured}
                className="w-full h-12 rounded-2xl bg-[#1A6B4F] hover:bg-[#14563F] text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                {isLoading ? 'Opening checkout…' : 'Checkout with Shopify'}
              </button>
              <button
                onClick={() => onNavigate('shop')}
                className="w-full h-11 rounded-2xl bg-secondary hover:bg-muted text-foreground"
              >
                Continue shopping
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function MessageCard({ title, body }: { title: string; body: string }) {
  return (
    <div className="bg-card border border-border rounded-2xl p-4">
      <div className="text-sm font-medium">{title}</div>
      <div className="text-sm text-muted-foreground mt-1">{body}</div>
    </div>
  );
}